<!-- 
Dialog for running a provider task against multiple providers.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog lg">
    <v-card v-if="show">
      <v-toolbar color="secondary" flat dark>
        <v-toolbar-title>
          <v-icon v-if="task.type" class="mr-2">{{ getIcon(task.type) }}</v-icon>
          {{ taskName }}
        </v-toolbar-title>
        <v-spacer/>
        <our-user-guide-button topic="levy-collection-workflow" section="running-a-levy-collection-provider-task"/>
      </v-toolbar>
      <v-card-text>
        <our-loading-indicator v-if="state.loading"/>
        <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
        <p v-else v-html="task.description" />
        <p v-if="state.loaded">
          {{ selectionCaption }}
          (The task is applicable to {{ pluralise(task.applicableCount) }} at the current time.)
        </p>
        <v-layout justify-center>
          <v-flex shrink class="hidden-md-and-up">
            <v-btn 
              :disabled="busy || !anyFavouritesNotSelected" 
              flat color="primary" class="ma-0"
              @click.stop="selectFavourite"
            >
              Tick favourite providers
            </v-btn>
          </v-flex>
        </v-layout>
        <v-data-table 
          v-if="state.loaded" 
          ref="table"
          v-model="selectedProviders"
          :headers="headers" 
          :items="providers" 
          :select-all="!processing" 
          item-key="id" 
          style="max-height:300px; overflow-y:auto" 
          hide-actions 
          must-sort
        >
          <template v-slot:items="props">
            <tr :ref="`row${props.item.providerId}`" :class="processing ? 'noselect' : ''" @click="toggle(props.item)">
              <td>
                <v-checkbox v-if="!processing" :value="props.selected" color="primary" hide-details/>
                <v-progress-circular 
                  v-if="isRunning(props.item.providerId)" 
                  color="primary" size="24" value="80"
                  indeterminate
                />
                <v-icon v-if="isCompleted(props.item.providerId)" color="success">check</v-icon>
                <v-icon v-if="isPending(props.item.providerId) && !anyFailures" color="default">hourglass_empty</v-icon>
                <v-tooltip v-if="isPending(props.item.providerId) && anyFailures" bottom>
                  <v-icon slot="activator" color="orange">clear</v-icon>
                  <span>The task was not run against this provider due to an error when running against another provider.</span>
                </v-tooltip>
                <v-icon v-if="isSkipped(props.item.providerId)" color="#f0f0f0">remove</v-icon>
                <v-tooltip v-if="isFailed(props.item.providerId)" bottom>
                  <v-icon slot="activator" color="red">error_outline</v-icon>
                  <span>The task failed when run against this provider. Check exception logs for details.</span>
                </v-tooltip>
              </td>
              <td>
                {{ props.item.providerName }}
              </td>
              <td>{{ props.item.providerCode }}</td>
              <td>
                <our-favourite-button :lcp="props.item" />
              </td>
            </tr>
          </template>
        </v-data-table>
        <p class="font-weight-bold text-xs-center mt-2 mb-0 pb-0">
          <span v-if="processing">{{ runningCaption }}</span>&nbsp;
          <v-progress-circular 
            v-if="finishing" 
            color="primary" size="24" value="80"
            indeterminate
          />
        </p>
      </v-card-text>
      <v-card-actions class="hidden-sm-and-down">
        <v-btn 
          :disabled="busy || !anyFavouritesNotSelected" flat color="secondary"
          class="ma-0"
          @click.stop="selectFavourite"
        >
          Tick favourite providers
        </v-btn>
        <v-spacer/>
        <v-btn v-if="!anyFailures && processing && !finishing" :disabled="stopping" flat @click="stopping=true">Stop</v-btn>
        <v-btn v-if="!anyFailures && !processing" :disabled="busy" flat @click="hide">Cancel</v-btn>
        <v-btn v-if="anyFailures" flat @click="hide">Close</v-btn>
        <v-btn 
          v-if="!anyFailures" 
          :disabled="busy || !anySelectedProviders" 
          flat color="success" 
          @click="run"
        >
          {{ buttonCaption }}
        </v-btn>
      </v-card-actions>
      <v-card-actions class="hidden-md-and-up">
        <v-spacer/>
        <v-btn 
          v-if="!anyFailures && processing && !finishing" 
          :disabled="stopping" 
          flat 
          @click="stopping=true"
        >
          Stop
        </v-btn>
        <v-btn 
          v-if="!anyFailures && !processing" 
          :disabled="busy" 
          flat 
          @click="hide"
        >
          Cancel
        </v-btn>
        <v-btn v-if="anyFailures" block flat @click="hide">Close</v-btn>
        <v-btn 
          v-if="!anyFailures" 
          :disabled="busy || !anySelectedProviders" 
          flat color="success" 
          @click="run"
        >
          Run
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as action from "@/store/levy-collection-providers/action-types";
import lookupMixins from "@/mixins/lookup.mixins";
import taskMixins from "@/mixins/task.mixins";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR_HIDE } from "@/store/system/mutation-types";

export default {
  mixins: [lookupMixins, taskMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      type: Number,
      default: null
    },
    task: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedProviders: [],
      processing: false,
      stopping: false,
      runningProviderId: null,
      completedProviderIds: [],
      failedProviderIds: []
    };
  },
  computed: {
    headers() {
      const h = [];
      if (this.processing) {
        h.push({ sortable: false, width: 100 });
      }
      h.push({ text: "Legal name", sortable: true, value: "providerName" });
      h.push({ text: "Provider code", sortable: true, value: "providerCode", width: 10 });
      h.push({ text: "Favourite", value: "favourite", sortable: true, width: 10 });
      return h;
    },
    state() {
      return this.$store.state.levyCollectionProviders.forTask;
    },
    providers() {
      return this.state.data;
    },
    anySelectedProviders() {
      return this.selectedProviders.length > 0;
    },
    anyFailures() {
      return this.failedProviderIds.length > 0;
    },
    anyFavouritesNotSelected() {
      var notSelected = this.providers.filter(p => p.favourite).map(p => p.providerId);
      notSelected = notSelected.filter(p => !this.selectedProviderIds.includes(p));
      return notSelected.length > 0;
    },
    selectedProviderIds() {
      return this.selectedProviders.map(p => p.providerId);
    },
    busy() {
      return this.processing || this.state.loading;
    },
    taskName() {
      return this.lookups.providerTaskTypeDescriptions[this.task.type];
    },
    buttonCaption() {
      return this.anySelectedProviders
        ? `${this.taskName} for ${this.pluralise(this.selectedProviders.length)}`
        : this.taskName;
    },
    selectionCaption() {
      const total = this.selectedProviders.length;
      if (!this.anySelectedProviders) {
        return "Select the providers to run the task against, or click the first checkbox to choose all providers.";
      } else if (total < this.task.applicableCount) {
        return `You have selected ${this.pluralise(total)}.`;
      } else {
        return "You have selected all providers.";
      }
    },
    runningCaption() {
      const total = this.selectedProviders.length;
      const completed = this.completedProviderIds.length;
      if (this.processing) {
        if (this.stopping) {
          return `Cancelling...`;
        } else if (this.anyFailures) {
          return `Failed: completed ${completed} of ${total}.`;
        } else if (completed < total) {
          return `Running: completed ${completed} of ${total}.`;
        } else {
          return "Tasks ran successfully, finishing up...";
        }
      } else {
        return "Not yet started";
      }
    },
    finishing() {
      const total = this.selectedProviders.length;
      const completed = this.completedProviderIds.length;
      return this.processing && completed == total;
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.reset();
      }
    }
  },
  methods: {
    async reset() {
      this.processing = false;
      this.stopping = false;
      this.runningProviderId = null;
      this.completedProviderIds = [];
      this.failedProviderIds = [];
      this.selectedProviders = [];
      const info = { levyCollectionId: this.levyCollectionId, taskType: this.task.type };
      const successful = await this.$store.dispatch(action.TASK_GET_PROVIDERS, info);
      if (!successful) {
        this.hide();
      }
    },
    async run() {
      this.processing = true;
      this.completedProviderIds = [];
      this.failedProviderIds = [];
      const container = this.$refs.table;
      for (let provider of this.providers) {
        const providerId = provider.providerId;
        const target = this.$refs[`row${providerId}`];
        if (this.selectedProviderIds.includes(providerId)) {
          this.$vuetify.goTo(target.offsetTop, { container, offset: 60 });
          this.runningProviderId = providerId;
          const info = {
            type: this.task.type,
            levyCollectionId: this.levyCollectionId,
            providerId: providerId
          };
          const successful = await this.$store.dispatch(action.TASK_RUN_QUIETLY, info);
          this.runningProviderId = null;
          if (successful) {
            this.completedProviderIds.push(providerId);
          } else {
            this.failedProviderIds.push(providerId);
            break;
          }
        }
        if (this.stopping) {
          break;
        }
      }
      if (!this.anyFailures) {
        this.$store.dispatch(action.TASK_RUN_FINISH_UP);
        this.$store.commit(
          SHOW_NOTIFICATION,
          `${this.taskName} completed successfully for ${this.pluralise(this.completedProviderIds.length)}.`
        );
        this.hide();
      }
    },
    hide() {
      if (this.anyFailures) {
        this.$store.dispatch(action.TASK_RUN_FINISH_UP);
        this.$store.commit(GLOBAL_ERROR_HIDE);
      }
      this.$emit("update:show", false);
    },
    isPending(providerId) {
      return (
        this.processing &&
        this.selectedProviderIds.includes(providerId) &&
        !this.completedProviderIds.includes(providerId) &&
        !this.isRunning(providerId) &&
        !this.isFailed(providerId)
      );
    },
    isCompleted(providerId) {
      return this.processing && this.completedProviderIds.includes(providerId);
    },
    isRunning(providerId) {
      return this.processing && providerId == this.runningProviderId;
    },
    isSkipped(providerId) {
      return this.processing && !this.selectedProviderIds.includes(providerId);
    },
    isFailed(providerId) {
      return this.processing && this.failedProviderIds.includes(providerId);
    },
    toggle(provider) {
      if (!this.processing) {
        const index = this.selectedProviders.indexOf(provider);
        if (index !== -1) {
          this.selectedProviders.splice(index, 1);
        } else {
          this.selectedProviders.push(provider);
        }
      }
    },
    getRowClass(providerId) {
      let c = `row-${providerId}`;
      return this.processing ? "noselect " + c : c;
    },
    selectFavourite() {
      var favourites = [];
      this.state.data.forEach(p => {
        if (p.favourite && !this.selectedProviderIds.includes(p.providerId)) {
          favourites.push(p);
        }
      });
      if (favourites.length) {
        favourites.forEach(provider => {
          this.selectedProviders.push(provider);
        });
      }
    }
  }
};
</script>
